import { z } from '@repo/schema'
import {
  addPhoneWithCountryValidation,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
} from '@repo/schema/phone'

const WEBSITE_PATTERN = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i

const optionalEmail = () =>
  z
    .string()
    .trim()
    .refine(value => !value || z.email().safeParse(value).success, 'Please enter a valid email')

const optionalWebsite = () =>
  z
    .string()
    .trim()
    .refine(
      value => !value || WEBSITE_PATTERN.test(value),
      'Please enter a valid website, e.g. https://acme.co.uk',
    )

export const CreateCompanyTypeSchema = z.enum(['broker', 'haulier', 'shipper'])

const CreateCompanyFormTypeSchema = CreateCompanyTypeSchema.or(z.literal(''))

export const CreateCompanySchema = z
  .object({
    address: z.object({
      address: z.string().trim(),
      address_second_line: z.string().trim(),
      city: z.string().trim(),
      postcode: z.string().trim(),
    }),
    country: z.string().trim().min(1, 'Country is required'),
    email: optionalEmail(),
    notes: z.string().trim(),
    organisation_id: z.number({ message: 'Please select an organisation' }),
    phone: createPhoneNumberFieldSchema(),
    phone_country: OptionalPhoneCountryCodeSchema,
    registration_number: z.string().trim(),
    title: z.string().trim().min(1, 'Legal name is required'),
    type: CreateCompanyFormTypeSchema.refine(value => value !== '', 'Company type is required'),
    vat: z.string().trim(),
    website: optionalWebsite(),
  })
  .superRefine((data, ctx) => {
    addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
  })

/** The create-company form holds no organisation until one is inherited or selected. */
type CreateCompanyFormValues = Omit<z.input<typeof CreateCompanySchema>, 'organisation_id'> & {
  organisation_id: null | number
}

export const createCompanyDefaultValues: CreateCompanyFormValues = {
  address: {
    address: '',
    address_second_line: '',
    city: '',
    postcode: '',
  },
  country: 'GB',
  email: '',
  notes: '',
  organisation_id: null,
  phone: '',
  phone_country: 'GB',
  registration_number: '',
  title: '',
  type: '',
  vat: '',
  website: '',
}
